import { Component, ErrorInfo, ReactNode } from "react";

import { datadogRum } from "@datadog/browser-rum";
import ErrorPage from "@/errors/ErrorPage";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/* An Application Level error boundary component that forwards errors to the DataDog.
 * Utilize the RUM Browser SDK addError() API.
 * The collected rendering errors contain a component stack, which is unminified with our uploaded sourcemaps.
 */
class ApplicationErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: any): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }
  // used to log error information
  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const renderingError = new Error(error.message);
    renderingError.name = "ApplicationLevelRenderingError";
    renderingError.stack = info.componentStack;
    renderingError.cause = error;

    datadogRum.addError(renderingError);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorPage />;
    }

    return this.props.children;
  }
}

export default ApplicationErrorBoundary;
